import React, { useState, useEffect, useRef, useMemo } from "react";

import { Button, Checkbox, Select, Tooltip } from "antd";

import HistoryDataTable from "widgets/HistoryDataTable";
import Toast from "widgets/Toast";

import {
  getTransactionDetailsByBillingReqIdApi,
  getSubTransactionDetailsByTransactionIdApi,
} from "apis/paymentService";

import { regenerateInvoiceByTxnId } from "apis/regenerateInvoiceService";

import useTenantHook from "hooks/useTenantHook";
import Text from "antd/lib/typography/Text";
import { getInvoiceBySubTxnId } from "apis/invoiceService";
import {
  EyeOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import styles from "./index.scss";
import { getRoles } from "../../../../shared/Auth";
import PropTypes from "prop-types";
const toast = Toast();

const paginationMeta = {
  defaultPageSize: 10,
  pageSize: 10,
  size: "small",
  showSizeChanger: false,
};

const TransactionTable = ({ billingRequestId }) => {
  const isSendInvoiceButtonVisible = useMemo(() => {
    return getRoles().includes("ROLE_STUDENT_BUDDY");
  }, []);

  const [isLoading, setIsLoading] = useState(0);
  const [transactionData, setTransactionData] = useState({});
  const [paginationOptions, setPaginationOptions] = useState(paginationMeta);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeExpRow, setActiveExpRow] = useState([]);
  const [activeSubtransactionTableData, setActiveSubtransactionTableData] =
    useState([]);

  const { currentTenantCode } = useTenantHook();

  const tableRef = useRef(null);
  /* eslint-disable react/prop-types */
  const transactionTableColumns = [
    {
      title: "Transaction ID",
      dataIndex: "id",
      key: "id",
      width: 180,
      fixed: "left",
    },
    {
      title: "Country",
      dataIndex: "regionCode",
      width: 90,
      key: "regionCode",
      render: (countryCode) => countryCode?.toUpperCase(),
    },
    {
      title: "Currency",
      dataIndex: "currencyCode",
      width: 90,
      key: "currencyCode",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 100,
      key: "amount",
    },
    {
      title: "Discount",
      dataIndex: "discountAmount",
      width: 100,
      key: "discount",
      render: (amt) => amt || 0,
    },
    {
      title: "Payment Gateway",
      dataIndex: "paymentGatewayCode",
      width: 130,
      key: "paymentGatewayCode",
    },
    {
      title: "Payment Mode",
      dataIndex: "paymentModeCode",
      width: 140,
      key: "paymentModeCode",
    },
    {
      title: "Payment Status",
      dataIndex: "status",
      width: 140,
      key: "paymentStatus",
      sorter: true,
    },
    {
      title: "Status Reason",
      dataIndex: "failureReason",
      width: 140,
      key: "failureReason",
      render: (reason) => {
        return reason?.length > 15 ? (
          <Tooltip title={reason}>
            {" "}
            <Text type="danger">{reason.slice(0, 15)}</Text>
          </Tooltip>
        ) : (
          <Text type="danger">{reason}</Text>
        );
      },
    },
    {
      title: "Offline uploaded",
      dataIndex: "isOfflineUploadedPayment",
      width: 120,
      key: "isOfflineUploadedPayment",
      render: (v) => (v === true ? "Yes" : v === false ? "No" : "-"),
    },
    {
      title: "Txn. Complete Date",
      dataIndex: "paymentCompletedDateMills",
      width: 140,
      key: "paymentCompletedDateMills",
      render: (date) => (date ? new Date(date).toLocaleString() : "-"),
    },

    {
      title: (
        <div className={styles.flex}>
          <span>Regenerate Invoice</span>
          {!isSendInvoiceButtonVisible && (
            <Tooltip
              title="Uncheck the checkbox box if you don't want to trigger emails while regenerating the invoice."
              placement="topLeft"
            >
              <InfoCircleOutlined />
            </Tooltip>
          )}
        </div>
      ),
      width: 140,
      key: "regenerateInvoice",
      fixed: "right",
      align: "center",
      render: ({ id, status }) => {
        const [triggerEmails, setTriggerEmail] = useState(true);

        useEffect(() => {
          if (isSendInvoiceButtonVisible) {
            setTriggerEmail(false);
          }
        }, [isSendInvoiceButtonVisible]);

        if (
          isSendInvoiceButtonVisible &&
          !["SUCCESS", "SUCCESS_AND_SETTLED"].includes(status)
        ) {
          return <>N/A</>;
        }
        return (
          <div
            className={styles.flex}
            style={{
              margin: "0 10px",
              width: "100%",
            }}
          >
            {!isSendInvoiceButtonVisible && (
              <Tooltip
                title="Uncheck this box if you don't want to trigger emails while regenerating the invoice."
                placement="topLeft"
              >
                <Checkbox
                  checked={triggerEmails}
                  onChange={(e) => setTriggerEmail(e.target.checked)}
                />
              </Tooltip>
            )}

            <Button
              icon={<ReloadOutlined />}
              type="link"
              onClick={() => regenerateInvoice(id, triggerEmails)}
              // style={{marginLeft:"-2px"}}
            >
              Regenerate
            </Button>
          </div>
        );
      },
    },
  ];
  const subTranxTableColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 140,
      fixed: "left",
    },
    {
      title: "Amount",
      dataIndex: "paidAmount",
      width: 80,
      key: "amount",
    },
    {
      title: "Discount",
      dataIndex: "discountAppliedAmount",
      width: 80,
      key: "discount",
      render: (amt) => amt || 0,
    },
    {
      title: "Partner",
      dataIndex: "partner",
      width: 100,
      key: "partner",
    },
    {
      title: "Txn. Complete Date",
      dataIndex: "subTransactionCompletedMillis",
      width: 100,
      key: "subTransactionCompletedMillis",
      render: (date) => (date ? new Date(date).toLocaleString() : "-"),
    },
    {
      title: "Payment Status",
      dataIndex: "status",
      width: 80,
      key: "status",
    },
    {
      title: "Settlement Status", // NA
      dataIndex: "settlementStatus",
      width: 80,
      key: "settlementStatus",
    },
    {
      title: "Settlement Date", // NA
      dataIndex: "settlementDateMillis",
      width: 80,
      key: "settlementDate",
      render: (date) => (date ? new Date(date).toLocaleString() : "-"),
    },
    {
      title: "Amount Settled", // NA
      dataIndex: "settledAmount",
      width: 80,
      key: "amountSettled",
    },
    {
      title: "Merchant UTR", // NA
      dataIndex: "merchantUtr",
      width: 80,
      key: "merchantUTR",
    },
    {
      title: "Invoice",
      width: 80,
      key: "invoice",
      fixed: "right",
      render: ({
        invoiceNumber,
        id,
        transactionId,
        settlementDateMillis,
        subTransactionCompletedMillis,
        status,
      }) => {
        if (
          isSendInvoiceButtonVisible &&
          !["SUCCESS", "SUCCESS_AND_SETTLED"].includes(status)
        ) {
          return <>N/A</>;
        }

        return invoiceNumber ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Button
              icon={<EyeOutlined />}
              type="link"
              onClick={() => fetchInvoiceAndDownload(id)}
              // style={{ margin: '0 auto' }}
            >
              View
            </Button>
          </div>
        ) : (
          "N/A"
        );
      },
    },
  ];

  const regenerateInvoice = (transactionId, triggerEmails) => {
    setIsLoading(true);
    regenerateInvoiceByTxnId(transactionId, triggerEmails)
      .then(() => {
        return toast.success("Invoice Regenerated successfully");
      })
      .catch((e) => {
        toast.error(`Failed to regenerate invoice: ${e?.message}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchInvoiceAndDownload = (id) => {
    setIsLoading(true);
    getInvoiceBySubTxnId(id)
      .then((res) => {
        toast.success("Invoice fetched successfully");
        const link = document.createElement("a");
        link.href = res.data.url;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((e) => {
        console.error("Failed to fetch invoice", e);
        toast.error(`Failed to fetch invoice: ${e?.message}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchTransactionDetails = ({
    billingReqId: billingRequestId,
    prev,
    currentPage = 1,
    sortByStatus = null,
  }) => {
    setIsLoading(true);

    getTransactionDetailsByBillingReqIdApi({
      tenantCode: currentTenantCode,
      billingReqId: billingRequestId,
      currentPage,
      sortByStatus,
    })
      .then(({ data }) => {
        const { content, totalElements } = data;
        const res = prev;
        content.forEach((el) => {
          res[el.id] = el;
        });
        setTransactionData(res);
        setPaginationOptions((prev) => ({
          ...prev,
          total: totalElements,
        }));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchSubTransactionDetails = (transactionId) => {
    setIsLoading(true);
    getSubTransactionDetailsByTransactionIdApi(transactionId, 500)
      .then(({ data }) => {
        const { content } = data;
        setActiveSubtransactionTableData(content);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const search = (billingReqId, cp = currentPage - 1, sortByStatus) => {
    setPaginationOptions(paginationMeta);
    fetchTransactionDetails({
      billingReqId: billingReqId,
      prev: {},
      currentPage: cp,
      sortByStatus,
    });
  };

  const updateCurrentPageAndFetchData = ({ current }, _, sorter) => {
    setCurrentPage(current);

    let sortByStatus = null;
    if (sorter.columnKey === "paymentStatus") {
      switch (sorter.order) {
        case "ascend":
          sortByStatus = "ASC";
          break;
        case "descend":
          sortByStatus = "DESC";
          break;

        default:
          sortByStatus = null;
          break;
      }
    }
    search(billingRequestId, current - 1, sortByStatus);
  };

  useEffect(() => {
    return () => {
      setTransactionData(() => {});
      setCurrentPage(1);
      setPaginationOptions(paginationMeta);
    };
  }, []);

  useEffect(() => {
    paginationOptions.total?.length < 5 && setPaginationOptions(false);
  }, [paginationOptions.total]);

  // Add this effect to fetch data when component mounts
  useEffect(() => {
    if (billingRequestId && currentTenantCode) {
      fetchTransactionDetails({
        billingReqId: billingRequestId,
        prev: {},
        currentPage: 0,
        sortByStatus: null,
      });
    }
  }, [billingRequestId, currentTenantCode]);

  return (
    <>
      <div ref={tableRef}>
        <HistoryDataTable
          titleTextAlign="start"
          scroll={{ x: false, y: 400 }}
          data={Object.values(transactionData)}
          columns={transactionTableColumns}
          title="Transaction History"
          loading={isLoading}
          pagination={{
            ...paginationOptions,
            current: currentPage,
          }}
          onChange={updateCurrentPageAndFetchData}
          rowKey="id"
          expandable={{
            expandedRowRender: (/* record, index, indent, expanded */) => {
              return isLoading ? null : (
                <HistoryDataTable
                  data={activeSubtransactionTableData}
                  columns={subTranxTableColumns}
                  title="Sub-Transaction(s)"
                  titleTextAlign="start"
                  loading={isLoading}
                />
              );
            },
            // rowExpandable: (record) => record.name !== 'Not Expandable',
            expandedRowKeys: activeExpRow, // expand only one row at any given time
            onExpand: (expanded, record) => {
              // expand only one row at any given time
              // https://stackoverflow.com/questions/70451798/expand-only-one-table-row-at-a-time-ant-design-react
              const keys = [];
              if (expanded) {
                keys.push(record.id);
                fetchSubTransactionDetails(record.id);
              }
              setActiveExpRow(keys);
            },
          }}
        />
      </div>
    </>
  );
};

export default TransactionTable;

TransactionTable.propTypes = {
  billingRequestId: PropTypes.string.isRequired,
};
